/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Seed Assets Inventory & 1-Click R2 Syncer View (Task 11.5.5)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Database,
  RefreshCw,
  Search,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  UploadCloud,
  ShieldCheck,
  Play,
  Pause,
  Copy,
  ExternalLink,
  FileCode,
  X,
} from 'lucide-react';
import { SophiaIcon } from '@sofia/assets';
import { useI18n } from '../i18n';
import { getAdminDomains } from '../lib/urls';
import { getAdminToken } from '../lib/api-client';
import type {
  SeedAssetItem,
  SeedAssetListResponse,
  SeedAssetSyncResponse,
  SeedAssetVerifyResponse,
} from '@sofia/shared-types';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

export const SeedAssetsView: React.FC = () => {
  const { t } = useI18n();
  const domains = getAdminDomains();

  const [assets, setAssets] = useState<SeedAssetItem[]>([]);
  const [summary, setSummary] = useState({
    total: 47,
    synced: 0,
    missing: 47,
    outdated: 0,
  });
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'SYNCED' | 'MISSING'>('all');

  // Audio Playback State
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Copy Feedback State
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Toast Notification
  const [toastMsg, setToastMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchSeedAssets = async () => {
    try {
      setLoading(true);
      const token = getAdminToken() || '';
      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/seed-assets`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = (await res.json()) as { success: boolean; data: SeedAssetListResponse };
      if (data.success && data.data) {
        setAssets(data.data.items);
        setSummary(data.data.summary);
      }
    } catch (err: any) {
      setToastMsg({
        type: 'error',
        text: err?.message || 'Failed to load seed assets',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSeedAssets();
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handleVerify = async () => {
    try {
      setVerifying(true);
      const token = getAdminToken() || '';
      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/seed-assets/verify`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });

      const result = (await res.json()) as { success: boolean; data?: SeedAssetVerifyResponse; error?: string };
      if (!res.ok || !result.success || !result.data) {
        throw new Error(result.error || `HTTP ${res.status}`);
      }

      setSummary(result.data.summary);
      await fetchSeedAssets();

      setToastMsg({
        type: 'success',
        text: t('admin.seedAssets.toast.verifySuccess', {
          synced: result.data.synced,
          total: result.data.total,
        }),
      });
    } catch (err: any) {
      setToastMsg({
        type: 'error',
        text: t('admin.seedAssets.toast.verifyError', { error: err?.message || 'Verification failed' }),
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const token = getAdminToken() || '';
      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/seed-assets/sync`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });

      const result = (await res.json()) as { success: boolean; data?: SeedAssetSyncResponse; error?: string };
      if (!res.ok || !result.success || !result.data) {
        throw new Error(result.error || `HTTP ${res.status}`);
      }

      setAssets(result.data.items);
      setSummary(result.data.summary);

      setToastMsg({
        type: 'success',
        text: t('admin.seedAssets.toast.syncSuccess', {
          count: result.data.syncedCount,
        }),
      });
    } catch (err: any) {
      setToastMsg({
        type: 'error',
        text: t('admin.seedAssets.toast.syncError', { error: err?.message || 'Sync failed' }),
      });
    } finally {
      setSyncing(false);
    }
  };

  const toggleAudio = (item: SeedAssetItem) => {
    if (playingKey === item.key) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setPlayingKey(null);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
    }

    const audio = new Audio(item.publicUrl);
    audioRef.current = audio;
    audio.onended = () => setPlayingKey(null);
    audio.onerror = () => {
      setPlayingKey(null);
      setToastMsg({
        type: 'error',
        text: 'Audio file not accessible. Sync to R2 first.',
      });
    };

    audio.play().catch(() => {
      setPlayingKey(null);
    });
    setPlayingKey(item.key);
  };

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const filteredAssets = assets.filter((asset) => {
    const matchesSearch =
      asset.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      asset.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === 'all' || asset.category === selectedCategory;
    const matchesStatus =
      statusFilter === 'all' || asset.status === statusFilter;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Toast Notification Banner */}
      {toastMsg && (
        <div
          className={`p-4 rounded-xl flex items-center justify-between text-xs font-medium border ${
            toastMsg.type === 'success'
              ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
              : 'bg-rose-500/10 border-rose-500/20 text-rose-400'
          }`}
        >
          <div className="flex items-center gap-2">
            {toastMsg.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 shrink-0" />
            )}
            <span>{toastMsg.text}</span>
          </div>
          <button
            onClick={() => setToastMsg(null)}
            className="hover:opacity-75 transition-opacity p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Database className="w-6 h-6 text-primary" />
            <h1 className="text-xl font-heading font-bold text-foreground">
              {t('admin.seedAssets.title')}
            </h1>
          </div>
          <p className="text-xs text-muted-fg mt-1">
            {t('admin.seedAssets.subtitle')}
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={fetchSeedAssets}
            disabled={loading}
            className="px-3 py-2 text-xs font-medium text-foreground bg-surface border border-surface-border rounded-xl hover:bg-surface-elevated transition-colors flex items-center gap-1.5 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </button>
          <button
            onClick={handleVerify}
            disabled={verifying || loading}
            className="px-3.5 py-2 text-xs font-semibold text-foreground bg-surface border border-surface-border hover:bg-surface-elevated rounded-xl transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <ShieldCheck className={`w-4 h-4 text-accent ${verifying ? 'animate-spin' : ''}`} />
            {verifying ? t('admin.seedAssets.actions.verifying') : t('admin.seedAssets.actions.verify')}
          </button>
          <button
            onClick={handleSync}
            disabled={syncing || loading}
            className="px-4 py-2 text-xs font-semibold text-white bg-primary hover:bg-primary-hover rounded-xl shadow-glow-primary transition-all flex items-center gap-2 disabled:opacity-50"
          >
            <UploadCloud className={`w-4 h-4 ${syncing ? 'animate-bounce' : ''}`} />
            {syncing ? t('admin.seedAssets.actions.syncing') : t('admin.seedAssets.actions.sync')}
          </button>
        </div>
      </div>

      {/* KPI Cards & Health Banner */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Assets */}
        <div className="p-5 bg-surface border border-surface-border rounded-2xl flex items-center justify-between">
          <div>
            <span className="text-xs text-muted-fg font-medium">
              {t('admin.seedAssets.summary.total')}
            </span>
            <div className="text-2xl font-heading font-extrabold text-foreground mt-1">
              {summary.total}
            </div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
            <SophiaIcon name="layer" size={20} className="text-primary" />
          </div>
        </div>

        {/* Synced in R2 */}
        <div className="p-5 bg-surface border border-surface-border rounded-2xl flex items-center justify-between">
          <div>
            <span className="text-xs text-muted-fg font-medium">
              {t('admin.seedAssets.summary.synced')}
            </span>
            <div className="text-2xl font-heading font-extrabold text-emerald-400 mt-1">
              {summary.synced}
            </div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
            <SophiaIcon name="structure-server" size={20} className="text-emerald-400" />
          </div>
        </div>

        {/* Missing in R2 */}
        <div className="p-5 bg-surface border border-surface-border rounded-2xl flex items-center justify-between">
          <div>
            <span className="text-xs text-muted-fg font-medium">
              {t('admin.seedAssets.summary.missing')}
            </span>
            <div
              className={`text-2xl font-heading font-extrabold mt-1 ${
                summary.missing === 0 ? 'text-muted-fg' : 'text-amber-400'
              }`}
            >
              {summary.missing}
            </div>
          </div>
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center border ${
              summary.missing === 0
                ? 'bg-surface-elevated border-surface-border text-muted-fg'
                : 'bg-amber-500/10 border-amber-500/20 text-amber-400'
            }`}
          >
            <SophiaIcon name="filter" size={20} />
          </div>
        </div>

        {/* Sync Status Percentage */}
        <div className="p-5 bg-surface border border-surface-border rounded-2xl flex items-center justify-between">
          <div>
            <span className="text-xs text-muted-fg font-medium">Health & Readiness</span>
            <div className="text-2xl font-heading font-extrabold text-accent mt-1">
              {Math.round((summary.synced / (summary.total || 1)) * 100)}%
            </div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center text-accent">
            <SophiaIcon name="hoverboard" size={20} className="text-accent" />
          </div>
        </div>
      </div>

      {/* Health Status Message Bar */}
      <div
        className={`p-4 rounded-xl flex items-center gap-3 text-xs font-medium border ${
          summary.missing === 0
            ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
            : 'bg-amber-500/10 border-amber-500/20 text-amber-400'
        }`}
      >
        {summary.missing === 0 ? (
          <CheckCircle2 className="w-4 h-4 shrink-0" />
        ) : (
          <AlertTriangle className="w-4 h-4 shrink-0" />
        )}
        <span>
          {summary.missing === 0
            ? t('admin.seedAssets.summary.statusHealthy')
            : t('admin.seedAssets.summary.statusDegraded', { count: summary.missing })}
        </span>
      </div>

      {/* Filter Toolbar */}
      <div className="bg-surface border border-surface-border rounded-2xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Category Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0 scrollbar-none">
          {[
            { id: 'all', label: t('admin.seedAssets.categories.all') },
            { id: 'voice', label: t('admin.seedAssets.categories.voice') },
            { id: 'bgm', label: t('admin.seedAssets.categories.bgm') },
            { id: 'sfx', label: t('admin.seedAssets.categories.sfx') },
            { id: 'brand', label: t('admin.seedAssets.categories.brand') },
            { id: 'avatar', label: t('admin.seedAssets.categories.avatar') },
            { id: 'template', label: t('admin.seedAssets.categories.template') },
          ].map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-xl whitespace-nowrap transition-all ${
                selectedCategory === cat.id
                  ? 'bg-primary text-white shadow-sm'
                  : 'bg-surface-elevated text-muted-fg hover:text-foreground border border-surface-border'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Search Input & Status Filter */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-fg" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('common.search')}
              className="pl-8 pr-3 py-1.5 text-xs bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary w-48 md:w-56"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-3 py-1.5 text-xs bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="all">{t('common.all')}</option>
            <option value="SYNCED">{t('admin.seedAssets.summary.synced')}</option>
            <option value="MISSING">{t('admin.seedAssets.summary.missing')}</option>
          </select>
        </div>
      </div>

      {/* Assets Inventory Table */}
      <div className="bg-surface border border-surface-border rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-surface-border bg-surface-elevated text-muted-fg font-medium text-[11px] uppercase tracking-wider select-none">
                <th className="py-3 px-4 w-16">{t('admin.seedAssets.table.preview')}</th>
                <th className="py-3 px-4">{t('admin.seedAssets.table.assetName')}</th>
                <th className="py-3 px-4">{t('admin.seedAssets.table.category')}</th>
                <th className="py-3 px-4">{t('admin.seedAssets.table.storageKey')}</th>
                <th className="py-3 px-4">{t('admin.seedAssets.table.size')}</th>
                <th className="py-3 px-4">{t('admin.seedAssets.table.status')}</th>
                <th className="py-3 px-4 text-right">{t('admin.seedAssets.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border text-xs">
              {filteredAssets.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-muted-fg">
                    {loading ? t('common.loading') : 'No matching seed assets found.'}
                  </td>
                </tr>
              ) : (
                filteredAssets.map((asset) => {
                  const isAudio =
                    asset.category === 'voice' ||
                    asset.category === 'bgm' ||
                    asset.category === 'sfx';
                  const isImage =
                    asset.category === 'brand' || asset.category === 'avatar';
                  const isPlaying = playingKey === asset.key;

                  return (
                    <tr
                      key={asset.key}
                      className="hover:bg-surface-elevated/50 transition-colors"
                    >
                      {/* Preview / Audio Player Column */}
                      <td className="py-3 px-4">
                        {isAudio ? (
                          <button
                            onClick={() => toggleAudio(asset)}
                            title={isPlaying ? 'Pause' : 'Play Preview'}
                            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                              isPlaying
                                ? 'bg-primary text-white shadow-glow-primary'
                                : 'bg-surface-elevated border border-surface-border text-muted-fg hover:text-foreground'
                            }`}
                          >
                            {isPlaying ? (
                              <Pause className="w-3.5 h-3.5 animate-pulse" />
                            ) : (
                              <Play className="w-3.5 h-3.5 ml-0.5" />
                            )}
                          </button>
                        ) : isImage ? (
                          <div className="w-8 h-8 rounded-lg bg-surface-elevated border border-surface-border flex items-center justify-center overflow-hidden p-1">
                            <img
                              src={asset.publicUrl}
                              alt={asset.key}
                              className="max-w-full max-h-full object-contain"
                              onError={(e) => {
                                (e.target as any).style.display = 'none';
                              }}
                            />
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded-lg bg-surface-elevated border border-surface-border flex items-center justify-center text-muted-fg">
                            <FileCode className="w-4 h-4" />
                          </div>
                        )}
                      </td>

                      {/* Asset & Description */}
                      <td className="py-3 px-4">
                        <div className="font-semibold text-foreground">
                          {asset.key.split('/').pop()}
                        </div>
                        <div className="text-[11px] text-muted-fg line-clamp-1 mt-0.5">
                          {asset.description}
                        </div>
                      </td>

                      {/* Category Badge */}
                      <td className="py-3 px-4">
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-semibold uppercase bg-surface-elevated border border-surface-border text-muted-fg">
                          {asset.category}
                        </span>
                      </td>

                      {/* Storage Key with 1-click Copy */}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1.5 font-mono text-[11px] text-muted-fg">
                          <span className="truncate max-w-[180px] md:max-w-xs">{asset.key}</span>
                          <button
                            onClick={() => copyToClipboard(asset.key, asset.key)}
                            className="hover:text-foreground p-0.5"
                            title={t('admin.seedAssets.table.copyKey')}
                          >
                            {copiedKey === asset.key ? (
                              <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                          </button>
                        </div>
                      </td>

                      {/* Size */}
                      <td className="py-3 px-4 font-mono text-[11px] text-muted-fg">
                        {asset.sizeBytes > 0 ? formatBytes(asset.sizeBytes) : '—'}
                      </td>

                      {/* R2 Status */}
                      <td className="py-3 px-4">
                        {asset.status === 'SYNCED' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                            <CheckCircle2 className="w-3 h-3" />
                            SYNCED
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20">
                            <AlertTriangle className="w-3 h-3" />
                            MISSING
                          </span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="py-3 px-4 text-right">
                        <a
                          href={asset.publicUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="p-1.5 text-muted-fg hover:text-foreground inline-flex items-center gap-1 rounded-lg hover:bg-surface-elevated transition-colors text-xs"
                          title={t('admin.seedAssets.table.openCdn')}
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export const SeedAssetsPage = SeedAssetsView;
